/**
 * LOT S2 — pure helpers shared by the "Import show details (AI)" modal and its
 * tests. No React, no network: every risky decision (what is ticked, what is
 * sent, whether the money group needs a confirmation) is deterministic so a
 * unit test can prove it instead of an eye.
 *
 * CONTRACT (mirrors `server/routes/ai.js` `import_show` and
 * `server/lib/ai-actions.js` `update_show_fields`):
 *  - the AI extraction is READ-ONLY; nothing is written until the user ticks
 *    fields and presses Import (one `POST /api/ai/chat/apply`);
 *  - a field is NEVER sent unless it was ticked — no default selection;
 *  - an empty value is never sent (the server keeps the existing value);
 *  - the money group requires `confirmed_money: true` in the SAME apply;
 *  - identifiers / parentage are never sent by this action.
 */
import type { AiProposedShowFields } from '../api';

/** Money / contractual fields — rendered in their own visually separated group.
 * Mirrors `SHOW_FIELDS_MONEY` in `server/lib/ai-actions.js`. */
export const SHOW_MONEY_FIELDS = [
  'guarantee',
  'fee_type',
  'currency',
  'deposit_amount',
  'payment_due_date',
  'payment_status',
  'tickets_sold',
] as const;

/** Identifiers are never written by `update_show_fields` — never sent either. */
export const SHOW_FORBIDDEN_FIELDS = [
  'id',
  'band_id',
  'tour_id',
  'venue_id',
  'destination_show_id',
] as const;

/** Display order: the safe, readable fields first, money last. Anything the
 * server may add later still shows up (appended, alphabetical). */
export const SHOW_FIELD_ORDER = [
  'name',
  'date',
  'time',
  'door_time',
  'status',
  'event_type',
  'capacity',
  'from_city',
  'to_city',
  'notes',
  'flight_notes',
] as const;

export interface ShowFieldEntry {
  field: string;
  value: string | number;
  isMoney: boolean;
}

export function isMoneyField(field: string): boolean {
  return (SHOW_MONEY_FIELDS as readonly string[]).includes(field);
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return !Number.isFinite(value) || value === 0;
  return false;
}

/** True when the proposed value would REPLACE something already on the show.
 * The UI must show a badge in that case — never a silent replacement. */
export function isReplaceWarning(current: unknown, proposed: unknown): boolean {
  if (isEmptyValue(current)) return false;
  if (proposed === undefined || proposed === null) return false;
  return String(current).trim() !== String(proposed).trim();
}

/** Ordered list of the proposed fields: safe group first, money group after. */
export function showFieldEntries(proposed: AiProposedShowFields | null | undefined): ShowFieldEntry[] {
  if (!proposed) return [];
  const known = new Set<string>([...SHOW_FIELD_ORDER, ...SHOW_MONEY_FIELDS]);
  const water = Object.keys(proposed).filter((f) => !known.has(f));
  const ordered = [
    ...SHOW_FIELD_ORDER.filter((f) => proposed[f] !== undefined),
    ...SHOW_MONEY_FIELDS.filter((f) => proposed[f] !== undefined),
    ...water.sort(),
  ];
  return ordered
    .filter((f) => !(SHOW_FORBIDDEN_FIELDS as readonly string[]).includes(f))
    .map((field) => ({ field, value: proposed[field] as string | number, isMoney: isMoneyField(field) }));
}

/** What is sent as `changes` to `POST /api/ai/chat/apply`.
 *  - ONLY ticked fields,
 *  - never an empty value,
 *  - never an identifier. */
export function buildShowChanges(
  proposed: AiProposedShowFields | null | undefined,
  checked: Iterable<string>,
): Record<string, string | number> {
  const changes: Record<string, string | number> = {};
  if (!proposed) return changes;
  const ticked = new Set<string>();
  for (const f of checked) ticked.add(f);
  for (const field of ticked) {
    if ((SHOW_FORBIDDEN_FIELDS as readonly string[]).includes(field)) continue;
    const value = proposed[field];
    if (value === undefined || value === null) continue;
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) continue;
      changes[field] = s;
      continue;
    }
    if (typeof value === 'number' && Number.isFinite(value)) changes[field] = value;
  }
  return changes;
}

/** One money field ticked ⇒ the whole apply needs `confirmed_money: true`:
 * the server ignores every money value without it (silently, with a warning) —
 * so the client must never let that happen by omission. */
export function needsMoneyConfirmation(checked: Iterable<string>): boolean {
  for (const f of checked) if (isMoneyField(f)) return true;
  return false;
}

/** Nothing is ticked ⇒ nothing can be applied. */
export function canApplyShowImport(checked: Iterable<string>): boolean {
  for (const _ of checked) return true;
  return false;
}

/** Human-readable current/proposed value for the checklist. */
export function formatFieldValue(value: unknown): string {
  if (isEmptyValue(value)) return '—';
  return String(value);
}
